/**
 * Data hooks for EHR sync monitoring, over engage-v2-backend's
 * `listEhrSyncConnections` / `getEhrSyncConnection` (fed by the QIE Management API).
 * Maps the snake_case, PascalCase-enum backend rows onto @models/ehr-sync.
 */
import type { ApolloError } from '@apollo/client';
import { useMemo } from 'react';

import { useResponseHandler } from '@hooks/useResponseHandler';

import type {
  EhrEnvironmentKind,
  EhrResourceState,
  EhrSyncConnection,
  EhrSyncRegistryState as UiRegistryState,
  EhrSyncPolicyState,
  EhrSyncDirection,
  EhrSyncResource,
  EhrSyncResult,
  EhrSyncRun,
  EhrSyncStatus,
} from '@models/ehr-sync';

import {
  useGetEhrSyncConnectionQuery,
  useListEhrSyncConnectionsQuery,
  useUpsertEhrSyncResourceMutation,
} from 'generated/ehr-sync.generated';
import type { EhrSyncConnectionFieldsFragment } from 'generated/ehr-sync.generated';
import {
  EhrEnvironment,
  EhrSyncRegistryState,
  EhrSyncResourcePolicyState,
} from 'generated/types';

// These hooks use the raw generated queries on purpose. The `useResponseHandler`
// wrappers default to the error boundary, which would replace the whole page.
// A monitoring page has to stay up when the thing it monitors is down, so the
// views render the error inline and classify it with `isServiceUnreachable`.

type RawConnection = EhrSyncConnectionFieldsFragment;
type RawResource = RawConnection['resources'][number];
type RawRun = NonNullable<RawConnection['history']>[number];

const REGISTRY_STATE: Record<EhrSyncRegistryState, UiRegistryState> = {
  [EhrSyncRegistryState.Active]: 'active',
  [EhrSyncRegistryState.Provisioning]: 'provisioning',
  [EhrSyncRegistryState.Disabled]: 'disabled',
  [EhrSyncRegistryState.ZoneNotFound]: 'zoneNotFound',
  [EhrSyncRegistryState.ZoneNameMismatch]: 'zoneNameMismatch',
};

const EHR_ENVIRONMENT: Record<EhrEnvironment, EhrEnvironmentKind> = {
  [EhrEnvironment.Sandbox]: 'sandbox',
  [EhrEnvironment.NonProd]: 'nonProd',
  [EhrEnvironment.Production]: 'production',
};

const POLICY_STATE: Record<EhrSyncResourcePolicyState, EhrSyncPolicyState> = {
  [EhrSyncResourcePolicyState.Configured]: 'configured',
  [EhrSyncResourcePolicyState.NoPolicy]: 'noPolicy',
  [EhrSyncResourcePolicyState.ChannelNotFound]: 'channelNotFound',
};

const mapResource = (r: RawResource): EhrSyncResource => ({
  channelId: r.channel_id ?? null,
  channelName: r.channel_name,
  resourceType: r.resource_type,
  direction: r.direction
    ? (r.direction.toLowerCase() as EhrSyncDirection)
    : null,
  state: r.state ? (r.state.toLowerCase() as EhrResourceState) : null,
  stateDescription: r.state_description,
  isEnabled: r.is_enabled,
  receivedCount: r.received_count,
  completedCount: r.completed_count,
  errorQueue: r.error_queue,
  inboundQueue: r.inbound_queue,
  outboundQueue: r.outbound_queue,
  processingQueue: r.processing_queue,
  lastReceivedAt: r.last_received_at,
  lastErrorAt: r.last_error_at,
  stoppedNodes: r.stopped_nodes,
  scope: r.scope,
  gated: r.gated ?? undefined,
  ehrSyncResourceId: r.ehr_sync_resource_id ?? null,
  enabled: r.enabled,
  policyState: POLICY_STATE[r.policy_state],
  policyUpdatedAt: r.policy_updated_at ?? null,
});

const mapRun = (run: RawRun): EhrSyncRun => ({
  at: run.at,
  trigger: run.trigger,
  result: run.result.toLowerCase() as EhrSyncResult,
  durationLabel: run.duration_label,
  detail: run.detail,
});

/**
 * Maps a backend connection onto the UI model.
 * @param c A connection as returned by the GraphQL API
 * @returns The camelCase, lowercase-enum UI model
 */
export const mapEhrSyncConnection = (c: RawConnection): EhrSyncConnection => ({
  id: c.id,
  ehrConnectionId: c.ehr_connection_id,
  qieZoneName: c.qie_zone_name,
  ehrEnvironment: c.ehr_environment ? EHR_ENVIRONMENT[c.ehr_environment] : null,
  registryState: c.registry_state ? REGISTRY_STATE[c.registry_state] : null,
  lastReceivedAt: c.last_received_at,
  organizationId: c.organization_id,
  organizationName: c.organization_name,
  network: c.network,
  ehrVendor: c.ehr_vendor,
  environmentLabel: c.environment_label,
  description: c.description,
  status: c.status.toLowerCase() as EhrSyncStatus,
  lastSyncAt: c.last_sync_at,
  goLiveLabel: c.go_live_label,
  patientCount: c.patient_count,
  reviewCount: c.review_count ?? null,
  recordCount: c.record_count,
  completedCount: c.completed_count,
  errorCount: c.error_count,
  createdAt: c.created_at,
  modifiedAt: c.modified_at,
  errorSummary: c.error_summary
    ? { title: c.error_summary.title, body: c.error_summary.body }
    : null,
  resources: c.resources.map(mapResource),
  history: c.history ? c.history.map(mapRun) : undefined,
});

/**
 * True when the request never got a GraphQL response (the API or QIE behind it
 * did not answer). A GraphQL error such as Forbidden is a different failure and
 * MUST NOT be reported as the integration service being down.
 * @param error The error from the query hook
 * @returns Whether the failure was at the network layer
 */
export const isServiceUnreachable = (error?: ApolloError): boolean =>
  Boolean(error?.networkError);

interface UseEhrSyncConnectionsResult {
  data: EhrSyncConnection[];
  loading: boolean;
  error?: ApolloError;
  /** Re-runs the query. Wired to the error state's Retry action. */
  refetch: () => void;
}

/**
 * Every EHR connection the caller may see. The backend scopes rows by role.
 * @returns The mapped connections plus loading, error and refetch
 */
export const useEhrSyncConnections = (): UseEhrSyncConnectionsResult => {
  const response = useListEhrSyncConnectionsQuery({
    // So Retry shows the loader instead of a frozen error panel.
    notifyOnNetworkStatusChange: true,
  });

  const data = useMemo<EhrSyncConnection[]>(
    () =>
      (response.data?.listEhrSyncConnections ?? []).map(mapEhrSyncConnection),
    [response.data]
  );

  return {
    data,
    loading: response.loading,
    error: response.error,
    refetch: () => {
      void response.refetch();
    },
  };
};

interface UseEhrSyncConnectionResult {
  data?: EhrSyncConnection;
  loading: boolean;
  error?: ApolloError;
  /** Re-runs the query. Wired to the error state's Retry action. */
  refetch: () => void;
}

/**
 * One EHR connection by id, for the detail drilldown.
 * @param id The connection (QIE zone) id; the query is skipped when absent
 * @returns The mapped connection plus loading, error and refetch
 */
export const useEhrSyncConnection = (
  id?: string
): UseEhrSyncConnectionResult => {
  const response = useGetEhrSyncConnectionQuery({
    variables: { input: { id: id ?? '' } },
    skip: !id,
    notifyOnNetworkStatusChange: true,
  });

  const data = useMemo<EhrSyncConnection | undefined>(() => {
    const raw = response.data?.getEhrSyncConnection;
    return raw ? mapEhrSyncConnection(raw) : undefined;
  }, [response.data]);

  return {
    data,
    loading: response.loading,
    error: response.error,
    refetch: () => {
      void response.refetch();
    },
  };
};

/** Arguments to {@link useUpsertEhrSyncResource}'s setter. */
interface SetResourceEnabledArgs {
  ehrConnectionId: string;
  resourceType: string;
  enabled: boolean;
}

/**
 * Turns sync on or off for one resource type of a connection, then refetches the
 * connection so the policy table shows the stored row. Errors go to the snackbar;
 * success is left to the caller, which offers Undo.
 * @returns The setter, resolving true on success, and a loading flag
 */
export const useUpsertEhrSyncResource = (): [
  (args: SetResourceEnabledArgs) => Promise<boolean>,
  boolean,
] => {
  const { handleError } = useResponseHandler({ handlerType: 'snackbar' });
  const [mutate, { loading }] = useUpsertEhrSyncResourceMutation({
    refetchQueries: ['getEhrSyncConnection'],
    awaitRefetchQueries: true,
  });

  const setEnabled = async ({
    ehrConnectionId,
    resourceType,
    enabled,
  }: SetResourceEnabledArgs) => {
    try {
      const { data, errors } = await mutate({
        variables: {
          input: {
            ehr_connection_id: ehrConnectionId,
            resource_type: resourceType,
            enabled,
          },
        },
      });
      if (errors) {
        handleError(errors);
        return false;
      }
      return Boolean(data?.upsertEhrSyncResource);
    } catch (error) {
      handleError(error as ApolloError);
      return false;
    }
  };

  return [setEnabled, loading];
};
